import React from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';
import { storageKeys } from 'settings';

const names = {
  facebook: 'fb',
  twitter: 'twitter',
  mail: 'email',
  download: 'download',
};

/** Dedicated share host (e.g. https://westhollywoodshare.travpromobile.com/) — avoids default / share. subdomain logic. */
const resolveSharePageOrigin = (customDomain) => {
  const s = typeof customDomain === 'string' ? customDomain.trim() : '';
  if (!s) return null;
  try {
    const withSlash = s.endsWith('/') ? s : `${s}/`;
    return new URL(withSlash).origin;
  } catch {
    return null;
  }
};

const copyFunction = (str) => {
  const area = document.createElement('textarea');
  area.value = str;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.left = '-1000rem';
  document.body.appendChild(area);
  area.focus();
  area.select();
  document.execCommand('copy');
  document.body.removeChild(area);
};

const SharingPopup = ({ url, triggerId = '', title = '', image = '' }) => {
  let appInfoJson = null;
  try {
    const raw = localStorage.getItem(storageKeys.appInfo.name);
    appInfoJson = raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.warn('[SharingPopup] Invalid appInfo in localStorage:', e);
    appInfoJson = null;
  }

  const customShareDomain =
    (appInfoJson?.customShareDomain && String(appInfoJson.customShareDomain).trim()) ||
    (appInfoJson?.rawData?.customShareDomain && String(appInfoJson.rawData.customShareDomain).trim()) ||
    '';

  const shareOrigin = resolveSharePageOrigin(customShareDomain) || window.location.origin;

  let internalURL = url.length > 0 ? url.match(/[0-9]{3}.*?$/g) : '';
  if (url.includes('jwplatform')) {
    internalURL = url;
  }
  if (url.includes('youtube')) {
    internalURL = url;
  }
  const parsedURL = `${shareOrigin}/?file=${internalURL}`;

  // Creates a download url based on app_id, if above 499 use the new developer tool
  let downloadUrl = `https://cdn.travpromobile.com/apptool/app/${internalURL}`;
  if (internalURL && internalURL.toString().split('/')[0] > 499) {
    downloadUrl = `https://front.travpromobile.com/${internalURL}`;
  }

  if (url.includes('jwplatform')) {
    downloadUrl = url;
  }

  if (url.includes('youtube')) {
    downloadUrl = url;
  }

  let thumbnail = image;
  if (!image.includes('http')) {
    thumbnail = `https://front.travpromobile.com/${import.meta.env.VITE_APP_ID}/${image}`;
  }
  let urlEncoded = url.length > 0 ? parsedURL.replace(/ /g, '%20') : '';
  if (!customShareDomain) {
    urlEncoded = urlEncoded.replace('https://', 'https://share.');
    urlEncoded = urlEncoded.replace('http://', 'http://share.');
    urlEncoded = urlEncoded.replace('share.travprocdn', 'travprocdn');
    urlEncoded = urlEncoded.replace(/(https?:\/\/share\.)www\./, '$1');
  }
  urlEncoded = urlEncoded.replace(/ /g, '%20');

  urlEncoded = `${urlEncoded}&subject=${title}`;
  urlEncoded = `${urlEncoded}&image=${thumbnail}`;

  // Image // Infographic (pdf or png) // Video
  const extension = url.substring(url.lastIndexOf('.') + 1);

  const sharing = appInfoJson?.rawData ?? {};

  const videoCopy = sharing.video_email_copy;
  const videoSubject = sharing.video_email_subject;
  const imageCopy = sharing.image_email_copy;
  const imageSubject = sharing.image_email_subject;
  const twitterCopy = sharing.twitter_copy;
  let { shareCopy } = sharing;
  let shareSubject = sharing.shareTitle;

  if (extension.includes('png') || extension.includes('jpg')) {
    shareSubject = imageSubject;
    shareCopy = imageCopy;
  } else if (extension.includes('mp4')) {
    shareSubject = videoSubject;
    shareCopy = videoCopy;
  } else if (url.includes('youtube')) {
    shareSubject = videoSubject;
    shareCopy = videoCopy;
  }

  const hide = { display: triggerId ? 'none' : 'inline-block' };

  const downloadHref = APIHandler.getDownloadURL(downloadUrl, title || undefined);

  const onClickEvent = async (button) => {
    const appId = await DataHandler.matchAppId();
    const { email } = await StorageHandler.load('user');
    const name = names[button.querySelector('span').innerHTML.toLowerCase()];
    await APIHandler.trackEvent(appId, email, name === 'download' ? 'downloads' : 'shares', name, button.href);
  };

  if (!window.observers) window.observers = {};

  if (!window.observers[url]) {
    window.observers[url] = new MutationObserver(async (list, observer) => {
      for (const mutation of list) {
        if (mutation.target.querySelector('.fancybox-share')) {
          const element = document.querySelector('.fancybox-share');
          const input = element.querySelector('#shareURL');
          if (input) {
            input.onclick = async ({ target }) => {
              target.innerText = i18n.translate('sharing.copied');
              copyFunction(target.value);
              setTimeout(() => {
                target.innerText = i18n.translate('sharing.copyURL');
              }, 2000);
            };
          }

          const wrapperParagraph = document.querySelector('.wrapper__paragraph');
          let wrapper = {};
          if (wrapperParagraph && wrapperParagraph.children) {
            wrapper = wrapperParagraph.children;
          }
          Array.from(wrapper).forEach((button, i) => {
            wrapper[i].onclick = () => onClickEvent(button);
          });

          observer.disconnect();
          window.observers[url] = null;
        }
      }
    });
    window.observers[url].observe(document.body, { childList: true });
  }

  return (
    <div className="fancybox-share" id={triggerId || null} style={hide}>
      <h3>{i18n.translate('share.header')}</h3>
      <p className="wrapper__paragraph">
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${urlEncoded}`}
          className="fancybox-share__button fancybox-share__button--fb"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
            <path d="m287 456v-299c0-21 6-35 35-35h38v-63c-7-1-29-3-55-3-54 0-91 33-91 94v306m143-254h-205v72h196" />
          </svg>
          <span>{'Facebook'}</span>
        </a>
        <a
          href={`https://twitter.com/share?url=${urlEncoded}&text=${twitterCopy}`}
          className="fancybox-share__button fancybox-share__button--tw"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="x"
        >
          <FontAwesomeIcon icon={['fab', 'x-twitter']} />
          {/* <span>{'X'}</span> */}
        </a>
        <a
          href={`mailto:?subject=${shareSubject}&body=${shareCopy} ${urlEncoded}`}
          className="fancybox-share__button fancybox-share__button--pt"
        >
          <FontAwesomeIcon icon={['far', 'at']} />
          <span>{'Mail'}</span>
        </a>
        <a
          href={downloadHref}
          className="fancybox-download__button fancybox-download__button--pt"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FontAwesomeIcon icon={['far', 'download']} />
          <span>{i18n.translate('share.download')}</span>
        </a>
      </p>
      <p>
        <button id="shareURL" className="fancybox-share__input" type="button" value={urlEncoded}>
          {i18n.translate('sharing.copyURL')}
        </button>
      </p>
    </div>
  );
};

export default SharingPopup;

SharingPopup.propTypes = {
  url: PropTypes.string.isRequired,
  triggerId: PropTypes.string,
  image: PropTypes.string,
  title: PropTypes.string,
};
